<template>
  <v-container>
    <SimplePageHeader title="Deal Details" class="px-6 pr-6 mb-5">
      <template v-slot:right-buttons>
        <v-btn
          color="primary"
          @click="showJobOpeningInfoModal"
          v-if="zohoIntegrationSettings.integrationEnabled && dealCreationTypeId === CREATE_FROM_ZOHO"
          block
        >
          Job Opening Info.
        </v-btn>
      </template>
    </SimplePageHeader>
    <v-stepper
      v-model="currentStep"
    >
      <v-stepper-header>
        <v-stepper-step
          v-if="zohoIntegrationSettings.integrationEnabled"
          :step="CREATION_TYPE"
          :complete="status.CREATION_TYPE || dealCreationTypeId === CREATE_FROM_ZOHO"
          :editable="status.CREATION_TYPE || dealCreationTypeId === CREATE_FROM_ZOHO"
        >
          <span :style="currentStep === CREATION_TYPE ? selectedStepStyle :''">Creation</span>
        </v-stepper-step>
        <v-divider v-if="zohoIntegrationSettings.integrationEnabled"></v-divider>
        <v-stepper-step
          :step="CONTRACT"
          :complete="status.CONTRACT || dealCreationTypeId === CREATE_FROM_ZOHO"
          :editable="status.CONTRACT || dealCreationTypeId === CREATE_FROM_ZOHO"
        >
          <span :style="currentStep === CONTRACT ? selectedStepStyle:''">Contract</span>
        </v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step
          :step="CLIENT"
          :complete="status.CLIENT || dealCreationTypeId === CREATE_FROM_ZOHO"
          :editable="status.CONTRACT || dealCreationTypeId === CREATE_FROM_ZOHO"
        >
          <span :style="currentStep === CLIENT ? selectedStepStyle:''">Client</span>
        </v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step
          :step="CANDIDATE"
          :complete="status.CANDIDATE || dealCreationTypeId === CREATE_FROM_ZOHO"
          :editable="status.CLIENT || dealCreationTypeId === CREATE_FROM_ZOHO"
        >
          <span :style="currentStep === CANDIDATE ? selectedStepStyle:''">Candidate</span>
        </v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step
          :step="CONTRACT_DETAILS"
          :complete="status.CONTRACT_DETAILS || dealCreationTypeId === CREATE_FROM_ZOHO"
          :editable="status.CANDIDATE || dealCreationTypeId === CREATE_FROM_ZOHO"
        >
          <span :style="currentStep === CONTRACT_DETAILS ? selectedStepStyle:''">Contract Details</span>
        </v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step
          :step="SUMMARY"
          :complete="status.SUMMARY || dealCreationTypeId === CREATE_FROM_ZOHO"
          :editable="status.CONTRACT_DETAILS || dealCreationTypeId === CREATE_FROM_ZOHO"
        >
          <span :style="currentStep === SUMMARY ? selectedStepStyle:''">Summary</span>
        </v-stepper-step>

      </v-stepper-header>

      <v-stepper-items>
        <v-stepper-content :step="CREATION_TYPE">
          <DealCreationType
            ref="DealCreationType"
            :readonly="creationTypeReadonly"
            @complete="currentStep = dealCreationTypeCompleteHandler()"
            @importFromZoho="importFromZohoHandler"
          ></DealCreationType>
        </v-stepper-content>
        <v-stepper-content :step="CONTRACT">
          <DealContractType
            ref="DealContractType"
            :readonly="contractTypeReadonly"
            @complete="currentStep = dealContractCompleteHandler()"
            @goBack="() => this.currentStep = this.CREATION_TYPE"
          ></DealContractType>
        </v-stepper-content>
        <v-stepper-content :step="CLIENT">
          <DealClient
            ref="DealClient"
            :readonly="isReadOnlyDeal"
            @complete="currentStep = dealClientCompleteHandler()"
            @goBack="() => this.currentStep = this.CONTRACT"
          ></DealClient>
        </v-stepper-content>
        <v-stepper-content :step="CANDIDATE">
          <DealCandidate
            ref="DealCandidate"
            :readonly="isReadOnlyDeal"
            @complete="currentStep = dealCandidateCompleteHandler()"
            @goBack="() => this.currentStep = this.CLIENT"
          ></DealCandidate>
        </v-stepper-content>
        <v-stepper-content :step="CONTRACT_DETAILS">
          <DealContractor
            :readonly="isReadOnlyDeal"
            v-if="dealAssignmentType === CONTRACTOR"
            @complete="currentStep = dealContractDetailsCompleteHandler()"
            @goBack="() => this.currentStep = this.CANDIDATE"
          ></DealContractor>
          <DealPermanentPlacement
            :readonly="isReadOnlyDeal"
            v-if="dealAssignmentType === PERMANENT_PLACEMENT"
            @complete="currentStep = dealContractDetailsCompleteHandler()"
            @goBack="() => this.currentStep = this.CANDIDATE"
          ></DealPermanentPlacement>
        </v-stepper-content>
        <v-stepper-content :step="SUMMARY">
          <DealSummary ref="DealSummary"></DealSummary>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
    <ZRJobOpeningsInfoModal
      ref="ZRJobOpeningsInfoModal"
      :jobOpeningInfo="jobOpeningInfo"
    ></ZRJobOpeningsInfoModal>
  </v-container>
</template>

<script>
import { SimplePageHeader } from '@/components/pageComponents'
import {
  DealCreationType,
  DealContractType,
  ZRJobOpeningsInfoModal
} from '@/components/deals/dealComponents'
import DealClient from '@/components/deals/dealComponents/DealClient'
import DealCandidate from '@/components/deals/dealComponents/DealCandidate'
import DealContractor from '@/components/deals/dealComponents/DealContractor'
import DealPermanentPlacement from '@/components/deals/dealComponents/DealPermanentPlacement'
import DealSummary from '@/components/deals/dealComponents/DealSummary'
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { dealStatuses } from '@/lib/enum/dealStatuses'
import { CONTRACTOR, PERMANENT_PLACEMENT } from '@/lib/constants/dealAssignmentTypes'
import { wordsSimilarity } from '@/lib/miscellaneous'
import { HOURLY as HOURLY_PAY_RATE } from '@/lib/constants/payRateTypes'
import { HOURLY as HOURLY_BILL_RATE } from '@/lib/constants/billRateTypes'
import { CREATE_FROM_ZOHO } from '@/lib/constants/dealCreationTypes'
import { CONTRACT_TO_HIRE } from '@/lib/constants/contractTypes'
import { NEW_CLIENT, EXISTING_CLIENT } from '@/lib/constants/clientTypes'
import { NEW_CANDIDATE, EXISTING_CANDIDATE } from '@/lib/constants/candidateTypes'
import { nextTick } from 'vue'
import { ADMIN, MANAGER, SALES_PERSON } from '@/lib/constants/securityRoles'
import { userIsAdmin, getCurrentUser, userHasRoles } from '@/lib/authentication'

export default {
  components: {
    DealCreationType,
    DealSummary,
    DealPermanentPlacement,
    DealContractor,
    DealCandidate,
    DealClient,
    SimplePageHeader,
    DealContractType,
    ZRJobOpeningsInfoModal
  },
  data () {
    return {
      selectedStepStyle: 'border-bottom: 2px solid #1976d2; padding-bottom: 2px;',
      jobOpeningInfo: {
        client_Name: {
          name: ''
        },
        associated_Candidates: [],
        posting_Title: '',
        job_Type: '',
        salary: '',
        hourly_Rate: '',
        client_Rate: '',
        created_By: {
          name: ''
        },
        account_Manager: {
          name: ''
        },
        duration: '',
        duration_Type: ''
      },
      creationTypeReadonly: false,
      contractTypeReadonly: false,
      CREATION_TYPE: 0,
      CONTRACT: 1,
      CLIENT: 2,
      CANDIDATE: 3,
      CONTRACT_DETAILS: 4,
      SUMMARY: 5,
      CONTRACTOR,
      PERMANENT_PLACEMENT,
      status: {
        CREATION_TYPE: false,
        CONTRACT: false,
        CLIENT: false,
        CANDIDATE: false,
        CONTRACT_DETAILS: false,
        SUMMARY: false
      },
      currentStep: null,
      CREATE_FROM_ZOHO,
      NEW_CLIENT,
      EXISTING_CLIENT,
      NEW_CANDIDATE,
      EXISTING_CANDIDATE
    }
  },
  computed: {
    ...mapFields('settings', ['payRateSettings', 'zohoIntegrationSettings']),
    ...mapFields('deal', [
      'id',
      'dealCreationTypeId',
      'dealAssignmentType',
      'statusId',
      'dealClientId',
      'dealCandidateId',
      'dealAssignmentId',
      'zrJobOpeningId'
    ]),
    ...mapFields('dealClient', [
      'existingClientId',
      'name',
      'clientType'
    ]),
    ...mapFields('clients', ['clientList']),
    ...mapFields('dealCandidate',
      [
        'existingUserId',
        'firstName',
        'lastName',
        'emailAddress',
        'candidateType'
      ]),
    ...mapFields('users', ['userList']),
    ...mapFields('dealAssignment', [
      'jobTitle',
      'contractTypeId',
      'lengthOfContract',
      'payRateTypeId',
      'payRate',
      'billRateTypeId',
      'billRate',
      'workedHoursAverage',
      'overtimeThreshold',
      'overtimeRate'
    ]),
    ...mapFields('zohoIntegration', ['jobOpeningId', 'zRJobOpeningInfo']),
    dealIdParameter () {
      return this.$route.params.id
    },
    isApproved () {
      return this.statusId === dealStatuses.Approved
    },
    isNewDeal () {
      return this.statusId === dealStatuses.New
    },
    isReadOnlyAccess () {
      return !this.userHasRoles({ user: this.getCurrentUser(), allowedRoles: [ADMIN, MANAGER, SALES_PERSON] })
    },
    isReadOnlyDeal () {
      return this.isReadOnlyAccess || this.isApproved || (!userIsAdmin() && this.statusId === dealStatuses.New)
    }
  },
  methods: {
    userIsAdmin,
    getCurrentUser,
    userHasRoles,
    wordsSimilarity,
    ...mapActions('settings', ['getSettings']),
    ...mapActions('deal', ['getDeal', 'clearDeal']),
    ...mapActions('dealClient', ['getDealClient', 'clearDealClient']),
    ...mapActions('dealCandidate', ['getDealCandidate', 'clearDealCandidate']),
    ...mapActions('dealAssignment', ['getDealAssignment', 'clearDealAssignment']),
    ...mapActions('clients', ['getClients']),
    ...mapActions('users', ['getUsers']),
    ...mapActions('zohoIntegration', ['getJobOpeningById']),
    dealCreationTypeCompleteHandler () {
      this.creationTypeReadonly = true
      this.status.CREATION_TYPE = true
      return this.CONTRACT
    },
    dealContractCompleteHandler () {
      if (this.dealCreationTypeId !== CREATE_FROM_ZOHO) {
        this.contractTypeReadonly = true
      }
      this.status.CONTRACT = true
      return this.CLIENT
    },
    dealClientCompleteHandler () {
      this.status.CLIENT = true
      return this.CANDIDATE
    },
    dealCandidateCompleteHandler () {
      this.status.CANDIDATE = true
      return this.CONTRACT_DETAILS
    },
    dealContractDetailsCompleteHandler () {
      this.status.CONTRACT_DETAILS = true
      this.status.SUMMARY = true
      return this.SUMMARY
    },
    importFromZohoHandler (jobOpeningInfo) {
      this.zrJobOpeningId = jobOpeningInfo.id
      this.jobOpeningInfo = jobOpeningInfo
      this.populateContractType(jobOpeningInfo)
      this.populateClient(jobOpeningInfo)
      this.populateCandidate(jobOpeningInfo)
      this.populateContractDetails(jobOpeningInfo)
      this.currentStep = this.dealCreationTypeCompleteHandler()
    },
    populateContractType (jobOpeningInfo) {
      if (jobOpeningInfo.job_Type.toLowerCase().includes('full') ||
        jobOpeningInfo.job_Type.toLowerCase().includes('perm')) {
        this.dealAssignmentType = PERMANENT_PLACEMENT
        this.$refs.DealContractType.permPlacementClickedHandler()
      } else {
        this.dealAssignmentType = CONTRACTOR
        this.$refs.DealContractType.contractorClickedHandler()
      }
    },
    populateClient (jobOpeningInfo) {
      this.getClients().then(() => {
        // Get all the clients and find their names similarity against the name provided in the jobOpeningInfo
        let clientListTemp = [...this.clientList]
        clientListTemp = clientListTemp.map(obj => ({
          ...obj,
          similarity: this.wordsSimilarity(obj.name, jobOpeningInfo.client_Name.name)
        }))
        // Choose the client with the highest similarity
        let maxSimilarity = Math.max(...clientListTemp.map(obj => obj.similarity))
        let tempClient = clientListTemp.find(obj => obj.similarity === maxSimilarity)
        // If the similarity percentage is greater than 80% suggest this client as an existing one.
        if (tempClient.similarity >= 0.8) {
          this.existingClientId = tempClient.id
          this.clientType = this.EXISTING_CLIENT
          this.$refs.DealClient.clientIdChangeHandler(this.existingClientId)
        } else {
          // Otherwise suggest the client provided by jobOpeningInfo as a new client.
          this.name = jobOpeningInfo.client_Name.name
          this.clientType = this.NEW_CLIENT
        }
      })
    },
    populateCandidate (jobOpeningInfo) {
      // Get all the users in the system and find their names similarity against the name provided in the jobOpeningInfo
      if (!!jobOpeningInfo.associated_Candidates.length && jobOpeningInfo.associated_Candidates.length > 0) {
        this.getUsers().then(() => {
          let tempUsersList = [...this.userList]
          tempUsersList = tempUsersList.map(obj => ({
            ...obj,
            similarity: this.wordsSimilarity(
              obj.fullName,
              jobOpeningInfo.associated_Candidates[0].full_Name)
          }))
          // Choose the user with the highest similarity
          let maxSimilarity = Math.max(...tempUsersList.map(obj => obj.similarity))
          let tempUser = tempUsersList.find(obj => obj.similarity === maxSimilarity)
          // If the similarity percentage is greater than 80% suggest this user as an existing one.
          if (tempUser.similarity >= 0.8) {
            this.existingUserId = tempUser.id
            this.candidateType = this.EXISTING_CANDIDATE
            this.$refs.DealCandidate.userIdChangeHandler(this.existingUserId)
          } else {
            // Otherwise suggest the user provided by jobOpeningInfo as a new user.
            this.firstName = jobOpeningInfo.associated_Candidates[0].first_Name
            this.lastName = jobOpeningInfo.associated_Candidates[0].last_Name
            this.emailAddress = jobOpeningInfo.associated_Candidates[0].email
            this.candidateType = this.NEW_CANDIDATE
          }
        })
      }
    },
    populateContractDetails (jobOpeningInfo) {
      if (this.dealAssignmentType === CONTRACTOR) {
        if (jobOpeningInfo.hourly_Rate) {
          this.payRateTypeId = HOURLY_PAY_RATE
          this.payRate = jobOpeningInfo.hourly_Rate
        }
        if (jobOpeningInfo.client_Rate) {
          this.billRateTypeId = HOURLY_BILL_RATE
          this.billRate = jobOpeningInfo.client_Rate
        }
        if (!!jobOpeningInfo.duration && jobOpeningInfo.duration > 0 && jobOpeningInfo.duration_Type === 'Months') {
          this.contractTypeId = CONTRACT_TO_HIRE
          let length = parseInt(jobOpeningInfo.duration)
          this.lengthOfContract = isNaN(length) ? 0 : length
        }
      }
      if (this.dealAssignmentType === PERMANENT_PLACEMENT) {
        this.jobTitle = jobOpeningInfo.posting_Title
      }
    },
    showJobOpeningInfoModal () {
      this.$refs.ZRJobOpeningsInfoModal.showModal()
    }
  },
  async mounted () {
    await this.getSettings().then(async () => {
      if (this.dealIdParameter) {
        let tempCurrentStep = null
        await this.getDeal({ id: this.dealIdParameter }).then(async () => {
          if (this.dealCreationTypeId) {
            await nextTick()
            this.$refs.DealCreationType.disableButtons()
            tempCurrentStep = this.dealCreationTypeCompleteHandler()
          }

          if (this.dealAssignmentType) {
            if (this.statusId) {
              await nextTick()
              this.$refs.DealContractType.disableButtons()
            }
            tempCurrentStep = this.dealContractCompleteHandler()
          }

          if (this.dealClientId) {
            await this.getDealClient({ dealId: this.dealIdParameter }).then(() => {
              if (this.existingClientId) {
                this.clientType = this.EXISTING_CLIENT
              } else {
                this.clientType = this.NEW_CLIENT
              }
              tempCurrentStep = this.dealClientCompleteHandler()
            })
          } else {
            this.clearDealClient()
          }

          if (this.dealCandidateId) {
            await this.getDealCandidate({ dealId: this.dealIdParameter }).then(async () => {
              await nextTick()
              if (this.existingUserId) {
                this.candidateType = this.EXISTING_CANDIDATE
              } else {
                this.candidateType = this.NEW_CANDIDATE
              }
              tempCurrentStep = this.dealCandidateCompleteHandler()
            })
          } else {
            this.clearDealCandidate()
          }

          if (this.dealAssignmentId) {
            await this.getDealAssignment({ dealId: this.dealIdParameter }).then(() => {
              tempCurrentStep = this.dealContractDetailsCompleteHandler()
            })
          } else {
            this.clearDealAssignment()
            this.workedHoursAverage = this.payRateSettings.workedHoursAverageDefault
            this.overtimeThreshold = this.payRateSettings.overtimeThresholdDefault
            this.overtimeRate = this.payRateSettings.overtimeRateDefault
          }

          if (this.zrJobOpeningId) {
            this.jobOpeningId = this.zrJobOpeningId
            this.getJobOpeningById()
              .then(() => {
                this.jobOpeningInfo = this.zRJobOpeningInfo
              })
          }

          this.currentStep = tempCurrentStep
        })
      } else {
        this.clearDeal()
        this.clearDealClient()
        this.clearDealCandidate()
        this.clearDealAssignment()
        this.workedHoursAverage = this.payRateSettings.workedHoursAverageDefault
        this.overtimeThreshold = this.payRateSettings.overtimeThresholdDefault
        this.overtimeRate = this.payRateSettings.overtimeRateDefault
        if (!this.zohoIntegrationSettings.integrationEnabled) {
          this.$refs.DealCreationType.createFromScratchClickedHandler(false)
          this.currentStep = this.CONTRACT
        } else {
          this.currentStep = this.CREATION_TYPE
        }
      }
    })
  }
}
</script>
